import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef, OnInit, OnDestroy } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
  NbCardModule,
  NbInputModule,
  NbSpinnerModule,
  NbButtonModule,
  NbSelectModule,
  NbOptionModule,
  NbToggleModule,
  NbDatepickerModule,
} from "@nebular/theme";
import { TranslateModule } from "@ngx-translate/core";
import { KPI } from "../../../../@core/api";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { getColorHex } from "../../../../@core/utils/color.utils";
import { MaintenanceService } from "../../../../@core/utils";
import { defaultDatepickerDate, toDateValue, toIsoMonthStartDate } from "../../../utils/date.utils";
@Component({
  selector: "app-maintenance-tab",
  templateUrl: "./maintenance-tab.component.html",
  styleUrls: ["./maintenance-tab.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbButtonModule,
    FontAwesomeModule,
    NbInputModule,
    TranslateModule,
    NbSelectModule,
    NbOptionModule,
    NbToggleModule,
    NbDatepickerModule,
  ],
})
export class MaintenanceTabComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public asset: KPI.MappedAsset;
  @Input() public loading: boolean = false;
  @Input() public maintenanceManagementEnabled: boolean = false;
  @Input() public equipmentValidationEnabled: boolean = false;
  @Output() public formChange = new EventEmitter<{ form: UntypedFormGroup; initialValues: any }>();

  public form: UntypedFormGroup;
  public initialValues: any;
  public showMaintenanceContractIndicator: boolean = false;
  public maintenanceRecordId: number | null = null;
  public maintenanceCompanies: string[] = [];
  public filteredCompanies: string[] = [];
  private subscriptions: Subscription[] = [];
  public readonly toDateValue = toDateValue;
  public readonly defaultDatepickerDate = defaultDatepickerDate;
  private readonly toIsoDate = toIsoMonthStartDate;

  protected NUMBER_VALIDATORS = [
    Validators.pattern(/^-?\d+\.?\d*$/),
    Validators.pattern(/^(?!-0(\.0+)?$)-?(0|[1-9]\d*)(\.\d+)?$/)
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private maintenanceService: MaintenanceService
  ) {}

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["asset"] && this.asset) {
      this.initializeForm();
      this.setMaintenanceContractIndicator();
      this.loadMaintenanceCompanies();
    }
  }



  private setMaintenanceContractIndicator(): void {
    const maintenance = (this.asset as any).maintenance;
    const color = this.getMaintenanceContractColor();
    this.showMaintenanceContractIndicator = maintenance && maintenance.has_contract_color && !this.isColorWhite(color);
  }

  private initializeForm(): void {
    const maintenance = (this.asset as any).maintenance;

    this.maintenanceRecordId = maintenance && maintenance.id && maintenance.id > 0 ? maintenance.id : null;

    this.initialValues = {
      maintenance_contract:
        maintenance?.has_contract !== undefined && maintenance?.has_contract !== null
          ? (maintenance.has_contract ? "true" : "false")
          : "",
      maintenance_company: maintenance?.company || "",
      maintenance_level: maintenance?.level?.toString() || null,
      maintenance_contract_end_date: this.toMonthValue(maintenance?.contract_deadline),
      maintenance_responsible: maintenance?.responsibility || "",
      manufacturer_control_frequency: maintenance?.control_frequency || "",
      last_maintainer_control_date: this.toMonthValue(maintenance?.last_control_date),
      maintenance_comment: maintenance?.comment || "",
      maintenance_equipment: true
    };

    this.form = new UntypedFormGroup({
      maintenance_contract: new UntypedFormControl({ value: this.initialValues.maintenance_contract, disabled: false }),
      maintenance_company: new UntypedFormControl({ value: this.initialValues.maintenance_company, disabled: false }, [Validators.maxLength(200)]),
      maintenance_level: new UntypedFormControl({ value: this.initialValues.maintenance_level, disabled: false }),
      maintenance_contract_end_date: new UntypedFormControl({
        value: this.initialValues.maintenance_contract_end_date,
        disabled: false
      }),
      maintenance_responsible: new UntypedFormControl({
        value: this.initialValues.maintenance_responsible,
        disabled: false
      }),
      manufacturer_control_frequency: new UntypedFormControl({
        value: this.initialValues.manufacturer_control_frequency,
        disabled: false
      }),
      last_maintainer_control_date: new UntypedFormControl({
        value: this.initialValues.last_maintainer_control_date,
        disabled: false
      }),
      maintenance_comment: new UntypedFormControl({ value: this.initialValues.maintenance_comment, disabled: false }, [Validators.maxLength(512)]),
      maintenance_equipment: new UntypedFormControl({ value: true, disabled: false })
    });

    this.form.get('maintenance_contract')?.valueChanges.subscribe(() => {
      this.cdr.detectChanges();
    });

    setTimeout(() => {
      this.formChange.emit({ form: this.form, initialValues: this.initialValues });

      const maintenanceFields = [
        'maintenance_contract',
        'maintenance_company',
        'maintenance_level',
        'maintenance_contract_end_date',
        'maintenance_responsible',
        'manufacturer_control_frequency',
        'last_maintainer_control_date',
        'maintenance_comment',
        'maintenance_equipment'
      ];

      maintenanceFields.forEach(fieldName => {
        const control = this.form.get(fieldName);
        if (control) {
          this.subscriptions.push(
            control.valueChanges.subscribe(() => {
              this.formChange.emit({ form: this.form, initialValues: this.initialValues });
            })
          );
        }
      });
    }, 0);
  }

  public onDatepickerMonthChange(date: Date, fieldName: string): void {
    this.form.get(fieldName)?.setValue(this.toIsoDate(date));
    this.cdr.detectChanges();
  }

  public toMonthValue(dateString: string | null | undefined): string | null {
    if (!dateString) return null;
    return dateString.length >= 7 ? dateString.substring(0, 7) : null;
  }

  public formatMonthYear(dateValue: any): string {
    const date = this.toDateValue(dateValue);
    if (!date) return '-';
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }

  public getMaintenanceContractColor(): string {
    const maintenance = (this.asset as any).maintenance;
    const color = getColorHex(maintenance?.has_contract_color);
    return color;
  }

  public getMaintenanceContractBackgroundColor(): string {
    const contractValue = this.form?.get('maintenance_contract')?.value;
    if (contractValue === 'false') {
      return '#F9D9C7';
    }
    return '';
  }

  public getMaintenanceContractDeadlineColor(): string {
    const maintenance = (this.asset as any).maintenance;
    const color = getColorHex(maintenance?.contract_deadline_color);
    return color;
  }

  public getMaintenanceLastControlDateColor(): string {
    const maintenance = (this.asset as any).maintenance;
    const color = getColorHex(maintenance?.last_control_date_color);
    return color;
  }

  public isColorWhite(color: string): boolean {
    if (!color) return true;
    const normalizedColor = color.toLowerCase().trim();
    return (
      normalizedColor === "#ffffff" ||
      normalizedColor === "#fff" ||
      normalizedColor === "white" ||
      normalizedColor === "#ffffffff" ||
      normalizedColor === "rgb(255, 255, 255)" ||
      normalizedColor === "rgba(255, 255, 255, 1)"
    );
  }

  public getFieldBackgroundColor(_fieldName: string): string {
    return '';
  }

  public isFieldMissing(fieldName: string): boolean {
    if (!this.equipmentValidationEnabled || !this.maintenanceManagementEnabled) {
      return false;
    }

    const value = this.form?.get(fieldName)?.value;
    const maintenance = (this.asset as any).maintenance;

    if (!maintenance || maintenance === null) {
      return false;
    }

    switch (fieldName) {
      case 'maintenance_contract_end_date':
      case 'last_maintainer_control_date':
        return !value || value === null;
      case 'maintenance_contract':
      case 'maintenance_company':
      case 'maintenance_level':
      case 'maintenance_responsible':
      case 'manufacturer_control_frequency':
      case 'maintenance_equipment':
        return !value || value === '' || value === null;
      default:
        return !value || value === '';
    }
  }

  public getMissingDataCount(): number {
    if (!this.maintenanceManagementEnabled) {
      return 0;
    }
    
    if (!this.form) {
      return 0;
    }

    let missingCount = 0;
    
    const fieldsToCheck = [
      'maintenance_contract',
      'maintenance_company',
      'maintenance_level',
      'maintenance_responsible',
      'manufacturer_control_frequency',
      'maintenance_contract_end_date',
      'last_maintainer_control_date'
    ];

    fieldsToCheck.forEach(fieldName => {
      const value = this.form.get(fieldName)?.value;
      
      if (fieldName === 'maintenance_contract_end_date' || fieldName === 'last_maintainer_control_date') {
        if (!value || value === null) {
          missingCount++;
        }
      } else {
        if (!value || value === '' || value === null) {
          missingCount++;
        }
      }
    });

    return missingCount;
  }

  public createMaintenanceRecord(): Observable<KPI.AssetMaintenance> {
    const payload = this.maintenanceService.mapFormToPayload(this.form.value, this.asset.id);
    return this.maintenanceService.createAssetMaintenance(payload);
  }

  public updateMaintenanceRecord(): Observable<KPI.AssetMaintenance> {
    if (!this.maintenanceRecordId) {
      throw new Error("No maintenance record ID available for update");
    }
    const payload = this.maintenanceService.mapFormToUpdatePayload(this.form.value, this.asset.id);
    return this.maintenanceService.updateAssetMaintenance(this.maintenanceRecordId, payload);
  }

  public saveMaintenanceRecord(): Observable<KPI.AssetMaintenance> {
    if (this.maintenanceRecordId) {
      return this.updateMaintenanceRecord();
    } else {
      return this.createMaintenanceRecord();
    }
  }

  private loadMaintenanceCompanies(): void {
    this.maintenanceCompanies = (this.asset as any).maintenance_companies || [];
    this.filteredCompanies = [];
  }

  public filterCompanies(value: string): void {
    if (!value || value.trim() === '') {
      this.filteredCompanies = [];
    } else {
      this.filteredCompanies = this.maintenanceCompanies.filter(company =>
        company.toLowerCase().includes(value.toLowerCase())
      );
    }
  }

  public onCompanyInputChange(value: string): void {
    this.filterCompanies(value);
  }

  public onCompanyInputFocus(value: string): void {
    if (!value || value.trim() === '') {
      this.filteredCompanies = [...this.maintenanceCompanies];
    } else {
      this.filterCompanies(value);
    }
  }

  public selectCompany(company: string): void {
    this.form.get('maintenance_company')?.setValue(company);
    this.filteredCompanies = [];
  }

  public onCompanyInputBlur(): void {
    setTimeout(() => {
      this.filteredCompanies = [];
    }, 200);
  }
}
